import React, { useState, useEffect } from "react";
import { Link } from "@reach/router";
import { useSelector } from "react-redux";
import Markdown from "react-markdown";
import axios from "axios";
import classNames from "classnames";
import { makeStyles } from "@material-ui/styles";
import Typography from "@material-ui/core/Typography";
import { getNumberOfItems } from "./util";
import { IconUsers, IconLock, IconTicket } from "@tabler/icons-react";
let source;

const useStyles = makeStyles((theme) => ({
  registration: {},
  cardContainer: {
    width: "1024px",
    margin: "0 auto",
    padding: "16px",
    display: "grid",
    alignItems: "center",
    gridTemplateColumns: (p) =>
      p.itemCount > 0 ? `repeat(${p.itemCount}, 1fr)` : "1fr",
    justifyItems: "center",
    gridGap: "16px",
    "@media(max-width: 1024px)": {
      width: "100%",
    },
    "@media(max-width: 667px)": {
      gridTemplateColumns: (p) => "1fr",
      gridGap: "8px",
      padding: "8px",
    },
    "& a": {
      textDecoration: "none",
      "@media(max-width: 667px)": {
        width: "100%",
      },
    },
  },
  card: {
    width: "auto",
    height: "100%",
    color: theme.app.labelColor,
    border: `1px solid ${theme.app.borderColor}`,
    backgroundColor: theme.app.background[1],
    borderRadius: "8px",
    transition: theme.transitions.create(["background-color"], {
      duration: theme.transitions.duration.short,
      easing: theme.transitions.easing.easeInOut,
    }),
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
    "@media(max-width: 667px)": {
      width: "100%",
    },
  },
  link: {
    height: "100%",
  },
  cardHeader: {
    padding: "1rem 0 0.35rem 0",
    textAlign: "center",
    fontSize: "1.25rem",
    fontWeight: 500,
    display: "flex",
    justifyContent: "center",
  },
  cardContent: { padding: "16px", fontSize: "1rem", textAlign: "center" },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "16px",
  },
  icon: {
    width: "50px",
    height: "50px",
    "@media(max-width: 667px)": {
      width: "40px",
      height: "40px",
    },
  },
  indexFooter: {
    color: theme.app.labelColor,
    "& a": {
      color: theme.palette.primary.main,
      textDecoration: "none",
      textTransform: "uppercase",
      padding: "8px",
      fontSize: "0.8125rem",
      fontWeight: 500,
      borderRadius: "4px",
      letterSpacing: "0.02857em",
      lineHeight: 1.75,
      transition: theme.transitions.create(["background-color"], {
        duration: theme.transitions.duration.short,
        easing: theme.transitions.easing.easeInOut,
      }),
      "&:hover": {
        backgroundColor: theme.palette.action.hover,
      },
    },
  },
}));

const Registration = () => {
  const { config, language } = useSelector((state) => ({
    config: state.infrastructure.config,
    language: state.language,
  }));
  const { registerMember, registerTicket, hideNewPassword } = config;
  const itemCount = getNumberOfItems(
    registerMember,
    registerTicket,
    hideNewPassword
  );
  const classes = useStyles({ itemCount });
  const [text, setText] = useState("");
  const [indexFooter, setIndexFooter] = useState("");
  useEffect(() => {
    let CancelToken = axios.CancelToken;
    source = CancelToken.source();
    axios
      .get("/ee-static/members/index.md", { cancelToken: source.token })
      .then((response) => setText(response.data))
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.log(error);
        }
      });
    if (config.useIndexFooter) {
      axios
        .get("/ee-static/members/index-footer.md", {
          cancelToken: source.token,
        })
        .then((response) => setIndexFooter(response.data))
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.log(error);
          }
        });
    }
    return () => source.cancel("Component Is Unmounting");
  }, [config]);

  return (
    <div className={classNames(classes.registration, "registration__index")}>
      <Markdown
        source={text}
        escapeHtml={false}
        skipHtml={false}
        className="members-index-text"
      />
      <div
        className={classNames(
          classes.cardContainer,
          "registration__card-container"
        )}
      >
        {registerMember && (
          <Link
            to={`${process.env.PUBLIC_URL}/registermember`}
            className={classNames(classes.link, "registration__link")}
            aria-labelledby="register-member-label"
            aria-describedby="register-member-body"
          >
            <div className={classNames(classes.card, "registration__card")}>
              <span
                id="register-member-label"
                className={classNames(
                  classes.cardHeader,
                  "registration__card-header"
                )}
              >
                {language.Nav
                  ? language.Nav["Register_Member"]
                  : "Registrera medlem"}
              </span>
              <div
                className={classNames(
                  classes.iconContainer,
                  "registration__icon-container"
                )}
              >
                <IconUsers
                  className={classNames(classes.icon, "registration__icon")}
                />
              </div>
              <Typography
                component="div"
                id="register-member-body"
                className={classNames(
                  classes.cardContent,
                  "registration__card-content"
                )}
              >
                {language.CardInfo ? language.CardInfo["registerMember"] : ""}
              </Typography>
            </div>
          </Link>
        )}
        {registerTicket && (
          <Link
            to={`${process.env.PUBLIC_URL}/registerticket`}
            className={classNames(classes.link, "registration__link")}
            aria-labelledby="register-ticket-label"
            aria-describedby="register-ticket-body"
          >
            <div className={classNames(classes.card, "registration__card")}>
              <span
                id="register-ticket-label"
                className={classNames(
                  classes.cardHeader,
                  "registration__card-header"
                )}
              >
                {language.Nav
                  ? language.Nav["Register_Ticket"]
                  : "Registrera biljett"}
              </span>
              <div
                className={classNames(
                  classes.iconContainer,
                  "registration__icon-container"
                )}
              >
                <IconTicket
                  className={classNames(classes.icon, "registration__icon")}
                />
              </div>
              <Typography
                component="div"
                id="register-ticket-body"
                className={classNames(
                  classes.cardContent,
                  "registration__card-content"
                )}
              >
                {language.CardInfo ? language.CardInfo["registerTicket"] : ""}
              </Typography>
            </div>
          </Link>
        )}
        {!hideNewPassword && (
          <Link
            to={`${process.env.PUBLIC_URL}/requestpassword`}
            className={classNames(classes.link, "registration__link")}
            aria-labelledby="request-password-label"
            aria-describedby="request-password-body"
          >
            <div className={classNames(classes.card, "registration__card")}>
              <span
                id="request-password-label"
                className={classNames(
                  classes.cardHeader,
                  "registration__card-header"
                )}
              >
                {language.Nav
                  ? language.Nav["Request_Password"]
                  : "Byt lösenord"}
              </span>
              <div
                className={classNames(
                  classes.iconContainer,
                  "registration__icon-container"
                )}
              >
                <IconLock
                  className={classNames(classes.icon, "registration__icon")}
                />
              </div>
              <Typography
                component="div"
                id="request-password-body"
                className={classNames(
                  classes.cardContent,
                  "registration__card-content"
                )}
              >
                {language.CardInfo ? language.CardInfo["password"] : ""}
              </Typography>
            </div>
          </Link>
        )}
      </div>
      <Markdown
        source={indexFooter}
        escapeHtml={false}
        skipHtml={false}
        className={classNames(classes.indexFooter, "members-index-footer")}
      />
    </div>
  );
};

export default Registration;
